import { Controller } from "@hotwired/stimulus";

// The comparator list and the per-type filter-value classes, local to this controller - nothing
// else consumes them.
const COMPARATORS = [
  { symbol: "gte", label: "Greater Than Or Equal To" },
  { symbol: "lte", label: "Less Than Or Equal To" },
];
const COMPARATOR_MAP = {};
COMPARATORS.forEach((c) => { COMPARATOR_MAP[c.symbol] = c; });

class EmptyFilterValue {
  present() {
    return !!this.filterOption;
  }

  label() {
    return "Add Filters";
  }
}

class DropdownFilterValue extends EmptyFilterValue {
  constructor(filterOption, comparator, value) {
    super();
    this.filterOption = filterOption;
    this.comparator = comparator;
    if (filterOption && filterOption.collection) {
      this.selection = filterOption.collection.filter((e) => e[1] == value)[0]; // eslint-disable-line eqeqeq
    } else {
      this.selection = value;
    }
  }

  displayValue() {
    if (this.selection && this.selection.length > 0) {
      return this.selection[0];
    }
    return this.selection;
  }

  label() {
    return this.filterOption.label + ":";
  }
}

class DatepickerFilterValue extends EmptyFilterValue {
  constructor(filterOption, comparator, value) {
    super();
    this.filterOption = filterOption;
    this.comparator = comparator;
    this.datetime = value || "";
  }

  displayValue() {
    return this.datetime;
  }

  label() {
    let label = this.filterOption.label;
    if (this.comparator) {
      label += " " + this.comparator.label;
    }
    return label + ":";
  }
}

class PriceFilterValue extends EmptyFilterValue {
  constructor(filterOption, comparator, value) {
    super();
    this.filterOption = filterOption;
    this.comparator = comparator;
    if (value) {
      this.selection = parseFloat(String(value).replace(/\$/, ""));
    }
  }

  displayValue() {
    return "$" + this.selection;
  }

  label() {
    let label = this.filterOption.label;
    if (this.comparator) {
      label += " " + this.comparator.label;
    }
    return label + ":";
  }
}

const FilterValueFactory = {
  fromJson(filterHash, availableFilters) {
    const filters = Object.keys(filterHash).map((key) => {
      const filterOption = availableFilters.filter((f) => f.name == key)[0]; // eslint-disable-line eqeqeq
      const value = filterHash[key];

      if (value !== null && typeof value === "object" && !Array.isArray(value)) {
        return Object.keys(value).map((comparatorKey) => {
          const comparator = COMPARATOR_MAP[comparatorKey];
          if (filterOption.type === "datepicker") {
            return new DatepickerFilterValue(filterOption, comparator, value[comparatorKey]);
          } else if (filterOption.type === "price") {
            return new PriceFilterValue(filterOption, comparator, value[comparatorKey]);
          } else {
            console.warn("Unknown filterOption type: " + filterOption.type); // eslint-disable-line no-console
            return undefined;
          }
        });
      } else if (Array.isArray(value)) {
        // A stackable filter (e.g. Stage) comes back as an array: one row per value.
        return value.map((v) => new DropdownFilterValue(filterOption, null, v));
      } else {
        return new DropdownFilterValue(filterOption, null, value);
      }
    });

    return filters.flat().filter((f) => f !== undefined);
  },

  create(type, filterOption) {
    if (type == "dropdown") { // eslint-disable-line eqeqeq
      return new DropdownFilterValue(filterOption);
    } else if (type == "price") { // eslint-disable-line eqeqeq
      return new PriceFilterValue(filterOption);
    } else if (type == "datepicker") { // eslint-disable-line eqeqeq
      return new DatepickerFilterValue(filterOption);
    } else {
      throw "invalid filter type";
    }
  },

  empty() {
    return new EmptyFilterValue();
  },
};

// Owns both halves of the filter modal: the shell with its filter-button preview, and each row's
// filter-type switching. Rows are added and removed entirely client-side and only round-trip to
// the server on submit, so the two cannot usefully be split apart.
export default class extends Controller {
  static get targets() {
    return [
      "buttons",
      "rows",
      "error",
      "modal",
      "rowTemplate",
      "dropdownTemplate",
      "datepickerTemplate",
      "priceTemplate",
      "buttonTemplate",
      "pill",
    ];
  }

  static get values() {
    return { availableFilters: Array, currentFilters: Object };
  }

  connect() {
    this.hydrateRows();
    this.renderButtons();

    // Reset the filters when the modal is dismissed rather than submitted. Bootstrap announces
    // this through jQuery, not a native DOM event, so it stays a jQuery binding.
    if (this.hasModalTarget) {
      $(this.modalTarget).on("hidden.bs.modal.dynamicFilters", () => this.hydrateRows());
    }
  }

  disconnect() {
    if (this.hasModalTarget) {
      $(this.modalTarget).off(".dynamicFilters");
    }
    this.discardRows();
  }

  // Rebuilds `.current-filters` from the server-rendered data attributes, re-derived on every
  // open.
  hydrateRows() {
    const currentFilterValues = FilterValueFactory.fromJson(this.currentFiltersValue, this.availableFiltersValue);
    this.discardRows();
    currentFilterValues.forEach((filterValue) => this.appendRow(filterValue));
    this.refreshFieldAvailability();
  }

  // Rows own bootstrap-datetimepicker instances and jQuery `dp.change` bindings. Dropping the
  // nodes with `innerHTML = ""` leaves both behind, and this runs again on every modal close.
  discardRows() {
    $(this.rowsTarget).find(".datetime_picker").each((_, el) => {
      $(el).data("DateTimePicker")?.destroy();
    });
    $(this.rowsTarget).empty();
  }

  // Shows the filters the page was rendered with, not in-progress edits.
  // Removes pills restored from Turbo's cache first so they aren't drawn twice.
  renderButtons() {
    this.pillTargets.forEach((pill) => pill.remove());
    const currentFilterValues = FilterValueFactory.fromJson(this.currentFiltersValue, this.availableFiltersValue);
    currentFilterValues.forEach((filterValue) => {
      const clone = this.buttonTemplateTarget.content.cloneNode(true);
      clone.querySelector(".filter-label").textContent = filterValue.label();
      clone.querySelector(".value").textContent = filterValue.displayValue();
      this.buttonsTarget.appendChild(clone);
    });
  }

  appendRow(filterValue) {
    const rowFragment = this.rowTemplateTarget.content.cloneNode(true);
    const rowEl = rowFragment.firstElementChild;
    this.rowsTarget.appendChild(rowEl);

    const select = rowEl.querySelector("select.available-filters");
    this.populateAvailableFiltersSelect(select, filterValue.present() ? filterValue.filterOption : null);

    if (filterValue.present()) {
      this.renderDetail(rowEl, filterValue.filterOption, filterValue);
    }
  }

  populateAvailableFiltersSelect(select, selectedFilterOption) {
    select.innerHTML = "";
    const blank = document.createElement("option");
    blank.value = "";
    select.appendChild(blank);

    this.availableFiltersValue.forEach((fo) => {
      const option = document.createElement("option");
      option.value = fo.name;
      option.textContent = fo.label;
      if (selectedFilterOption && selectedFilterOption.name === fo.name) {
        option.selected = true;
      }
      select.appendChild(option);
    });
  }

  // `select.available-filters` change.
  selectFilter(event) {
    const select = event.target;
    const rowEl = select.closest(".row");
    const filterOption = this.availableFiltersValue.filter((f) => f.name === select.value)[0];
    this.refreshFieldAvailability();
    if (!filterOption) {
      rowEl.querySelector(".filter-detail").innerHTML = "";
      return;
    }
    const filterValue = FilterValueFactory.create(filterOption.type, filterOption);
    this.renderDetail(rowEl, filterOption, filterValue);
  }

  renderDetail(rowEl, filterOption, filterValue) {
    const detailEl = rowEl.querySelector(".filter-detail");
    detailEl.innerHTML = "";

    if (filterOption.type === "dropdown") {
      detailEl.appendChild(this.buildDropdown(filterOption, filterValue));
    } else if (filterOption.type === "datepicker") {
      const { fragment, afterAttach } = this.buildDatepicker(filterOption, filterValue);
      detailEl.appendChild(fragment);
      // bootstrap-datetimepicker needs a real, attached element to bind against -- wire it up now
      // that `appendChild` has put the node in the document, not while it was still a detached clone.
      afterAttach();
    } else if (filterOption.type === "price") {
      detailEl.appendChild(this.buildPrice(filterOption, filterValue));
    } else {
      console.warn("Unknown filterOption type: " + filterOption.type); // eslint-disable-line no-console
    }
  }

  buildDropdown(filterOption, filterValue) {
    const clone = this.dropdownTemplateTarget.content.cloneNode(true);
    const select = clone.querySelector("select");
    // Stackable filters submit as `filter[name][]` so every row's value reaches the server.
    select.name = "filter[" + filterOption.name + "]" + (filterOption.multiple ? "[]" : "");
    // A blank first option plus native `required` gives the "nothing chosen yet" gate.
    const blank = document.createElement("option");
    blank.value = "";
    select.appendChild(blank);
    (filterOption.collection || []).forEach(([label, value]) => {
      const option = document.createElement("option");
      option.value = value;
      option.textContent = label;
      if (filterValue.selection && filterValue.selection[1] == value) { // eslint-disable-line eqeqeq
        option.selected = true;
      }
      select.appendChild(option);
    });
    return clone;
  }

  buildDatepicker(filterOption, filterValue) {
    const clone = this.datepickerTemplateTarget.content.cloneNode(true);
    const hidden = clone.querySelector('input[type="hidden"]');
    const comparatorSelect = clone.querySelector("select");
    const textInput = clone.querySelector(".datetime_picker");

    this.populateComparatorSelect(comparatorSelect, filterValue.comparator);
    hidden.name = "filter[" + filterOption.name + "][" + (filterValue.comparator ? filterValue.comparator.symbol : "") + "]";
    hidden.value = filterValue.displayValue();

    const syncHidden = () => {
      filterValue.datetime = textInput.value;
      hidden.value = filterValue.displayValue();
      hidden.name = "filter[" + filterOption.name + "][" + (filterValue.comparator ? filterValue.comparator.symbol : "") + "]";
    };
    // Manual typing fires a native `input` event, but the picker widget announces its own
    // selection through jQuery's `dp.change`, so that one needs a jQuery binding.
    textInput.addEventListener("input", syncHidden);
    comparatorSelect.addEventListener("change", () => {
      filterValue.comparator = COMPARATOR_MAP[comparatorSelect.value];
      syncHidden();
    });

    const afterAttach = () => {
      $(textInput).datetimepicker(window.datetimepickerDefaults);
      // Set AFTER init: bootstrap-datetimepicker parses the field's current value with a strict
      // "MM/DD/YYYY hh:mmA Z" moment parse and blanks it when it doesn't match - which a value
      // that round-tripped through a URL query param generally won't.
      textInput.value = filterValue.displayValue();
      $(textInput).on("dp.change.dynamicFilters", (value) => {
        filterValue.datetime = value.date.format("MM/DD/YYYY hh:mmA z");
        textInput.value = filterValue.datetime;
        syncHidden();
      });
    };

    return { fragment: clone, afterAttach };
  }

  buildPrice(filterOption, filterValue) {
    const clone = this.priceTemplateTarget.content.cloneNode(true);
    const hidden = clone.querySelector('input[type="hidden"]');
    const comparatorSelect = clone.querySelector("select");
    const numberInput = clone.querySelector("input.numeric");

    this.populateComparatorSelect(comparatorSelect, filterValue.comparator);
    hidden.name = "filter[" + filterOption.name + "][" + (filterValue.comparator ? filterValue.comparator.symbol : "") + "]";
    hidden.value = Number.isNaN(filterValue.selection) ? "" : filterValue.displayValue();
    if (!Number.isNaN(filterValue.selection) && filterValue.selection !== undefined) {
      numberInput.value = filterValue.selection;
    }

    const syncHidden = () => {
      filterValue.selection = parseFloat(numberInput.value);
      hidden.value = Number.isNaN(filterValue.selection) ? "" : filterValue.displayValue();
      hidden.name = "filter[" + filterOption.name + "][" + (filterValue.comparator ? filterValue.comparator.symbol : "") + "]";
    };
    numberInput.addEventListener("input", syncHidden);
    comparatorSelect.addEventListener("change", () => {
      filterValue.comparator = COMPARATOR_MAP[comparatorSelect.value];
      syncHidden();
    });

    return clone;
  }

  populateComparatorSelect(select, selectedComparator) {
    const blank = document.createElement("option");
    blank.value = "";
    select.appendChild(blank);
    COMPARATORS.forEach((c) => {
      const option = document.createElement("option");
      option.value = c.symbol;
      option.textContent = c.label;
      if (selectedComparator && selectedComparator.symbol === c.symbol) {
        option.selected = true;
      }
      select.appendChild(option);
    });
  }

  addFilterRow() {
    this.appendRow(FilterValueFactory.empty());
    this.refreshFieldAvailability();
  }

  removeRow(event) {
    const rowEl = event.target.closest(".row");
    rowEl.remove();
    this.refreshFieldAvailability();
  }

  // A single-value dropdown that already has a row is disabled in every other row's field picker,
  // since a second row would silently overwrite it. Datepicker and price filters take one row per
  // comparator (a ≥ row and a ≤ row make a range), and `multiple` dropdowns stack, so both stay available.
  refreshFieldAvailability() {
    const selects = Array.from(this.rowsTarget.querySelectorAll("select.available-filters"));
    const singleValueNames = new Set(
      this.availableFiltersValue.filter((f) => f.type === "dropdown" && !f.multiple).map((f) => f.name)
    );

    selects.forEach((select) => {
      const usedElsewhere = new Set(selects.filter((other) => other !== select).map((other) => other.value));
      Array.from(select.options).forEach((option) => {
        option.disabled = singleValueNames.has(option.value) && usedElsewhere.has(option.value);
      });
    });
  }

  // Submit the form only once every required field is filled.
  search(event) {
    event.preventDefault();
    const requiredEls = Array.from(this.element.querySelectorAll("[required]"));
    const allGood = requiredEls.every((el) => el.value !== null && el.value !== "" && el.value !== "?");

    if (allGood) {
      // jQuery's `.submit()` runs the form's own submit handlers first; native `form.submit()`
      // would skip them.
      $(this.element).closest("form").submit();
    } else {
      this.errorTarget.classList.remove("hidden");
    }
  }
}
